import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Chip,
} from '@mui/material';
import { Polyline, Marker } from '@react-google-maps/api';
import { Route as RouteIcon, Clear as ClearIcon } from '@mui/icons-material';
import { formatJobDeliveryLine } from '../utils/jobMapsLink';
import { optimizeDrivingRouteByTime, formatDriveDuration } from '../utils/drivingRouteOptimization';

const DRIVER_START_ID = '__driver_location__';

export function buildSimulatableJobs(jobs, geocodedLocations) {
  if (!Array.isArray(jobs)) return [];
  return jobs
    .filter((job) => {
      const loc = geocodedLocations[job.id];
      return loc && loc.lat != null && loc.lng != null;
    })
    .map((job) => ({
      id: job.id,
      label: `${String(job.id).substring(0, 8)} — ${formatJobDeliveryLine(job) || job.job_location || 'Delivery'}`,
      position: geocodedLocations[job.id],
      dieselQuantity: Number(job.diesel_quantity) || 0,
      defQuantity: Number(job.def_quantity) || 0,
    }));
}

function buildEndpointOptions(depots, getDepotPosition, driverLocation) {
  const options = [];
  if (driverLocation?.lat != null && driverLocation?.lng != null) {
    options.push({
      id: DRIVER_START_ID,
      label: 'Current location (driver)',
      position: { lat: driverLocation.lat, lng: driverLocation.lng },
    });
  }
  (depots || []).forEach((depot) => {
    const position = getDepotPosition ? getDepotPosition(depot) : null;
    if (position?.lat != null && position?.lng != null) {
      options.push({
        id: `depot-${depot.id}`,
        label: depot.name || 'Fuel depot',
        position,
      });
    }
  });
  return options;
}

/**
 * Toolbar + dialog for route simulation. Call onSimulationChange when a route is computed or cleared.
 */
export function MapRouteSimulationPanel({
  jobs,
  geocodedLocations,
  depots,
  getDepotPosition,
  driverLocation,
  mapRef,
  onSimulationChange,
  sx,
}) {
  const simulatableJobs = useMemo(
    () => buildSimulatableJobs(jobs, geocodedLocations),
    [jobs, geocodedLocations]
  );

  const endpointOptions = useMemo(
    () => buildEndpointOptions(depots, getDepotPosition, driverLocation),
    [depots, getDepotPosition, driverLocation]
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [startId, setStartId] = useState('');
  const [endId, setEndId] = useState('');
  const [simError, setSimError] = useState('');
  const [simulating, setSimulating] = useState(false);
  const [activeSummary, setActiveSummary] = useState(null);

  useEffect(() => {
    if (!dialogOpen) return;
    setSelectedJobIds(simulatableJobs.map((j) => j.id));
    if (endpointOptions.length) {
      const defaultStart = endpointOptions[0].id;
      const defaultEnd = endpointOptions.length > 1 ? endpointOptions[endpointOptions.length - 1].id : defaultStart;
      setStartId(defaultStart);
      setEndId(defaultEnd);
    }
    setSimError('');
  }, [dialogOpen, simulatableJobs, endpointOptions]);

  const toggleJob = (jobId) => {
    setSelectedJobIds((prev) =>
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const handleClear = () => {
    setActiveSummary(null);
    onSimulationChange?.(null);
  };

  const fitMapToPath = (path) => {
    if (!mapRef?.current || !window.google?.maps || !path?.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach((p) => bounds.extend(p));
    mapRef.current.fitBounds(bounds, 48);
  };

  const handleRunSimulation = async () => {
    setSimError('');
    if (!selectedJobIds.length) {
      setSimError('Select at least one job to simulate.');
      return;
    }
    const startOpt = endpointOptions.find((o) => o.id === startId);
    const endOpt = endpointOptions.find((o) => o.id === endId);
    if (!startOpt || !endOpt) {
      setSimError('Choose a valid start and end point.');
      return;
    }

    const selectedJobs = simulatableJobs.filter((j) => selectedJobIds.includes(j.id));
    const waypoints = selectedJobs.map((j) => ({
      id: j.id,
      lat: j.position.lat,
      lng: j.position.lng,
      label: j.label,
    }));

    const dieselGallons = selectedJobs.reduce((sum, j) => sum + j.dieselQuantity, 0);
    const defGallons = selectedJobs.reduce((sum, j) => sum + j.defQuantity, 0);

    setSimulating(true);
    try {
      const routeResult = await optimizeDrivingRouteByTime(
        startOpt.position,
        endOpt.position,
        waypoints
      );

      const stops = routeResult.orderedStops.map((wp, idx) => ({
        order: idx + 1,
        id: wp.id,
        label: wp.label,
        position: { lat: wp.lat, lng: wp.lng },
      }));

      const simulation = {
        path: routeResult.path,
        stops,
        start: { ...startOpt.position, label: startOpt.label },
        end: { ...endOpt.position, label: endOpt.label },
        totalSeconds: routeResult.totalSeconds,
        totalMiles: routeResult.totalMiles,
        dieselGallons,
        defGallons,
      };

      setActiveSummary(simulation);
      onSimulationChange?.(simulation);
      setDialogOpen(false);
      fitMapToPath(simulation.path);
    } catch (err) {
      console.error('Route simulation failed:', err);
      setSimError(
        err?.message ||
          'Failed to compute optimized driving route. Check that Directions API is enabled for your API key.'
      );
    } finally {
      setSimulating(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ...sx }}>
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<RouteIcon />}
        onClick={() => setDialogOpen(true)}
        disabled={simulatableJobs.length === 0}
      >
        Simulate
      </Button>
      {activeSummary && (
        <>
          <Chip
            size="small"
            color="secondary"
            label={`Route: ${activeSummary.stops.length} stop${activeSummary.stops.length === 1 ? '' : 's'}, ${formatDriveDuration(activeSummary.totalSeconds)} drive (${activeSummary.totalMiles.toFixed(1)} mi)`}
          />
          {(activeSummary.dieselGallons > 0 || activeSummary.defGallons > 0) && (
            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              label={`Fuel: ${[
                activeSummary.dieselGallons > 0 ? `${activeSummary.dieselGallons.toFixed(1)} gal Diesel` : null,
                activeSummary.defGallons > 0 ? `${activeSummary.defGallons.toFixed(1)} gal DEF` : null,
              ]
                .filter(Boolean)
                .join(' + ')}`}
            />
          )}
          <Button size="small" startIcon={<ClearIcon />} onClick={handleClear}>
            Clear route
          </Button>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Simulate delivery route</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select active jobs and fixed start/end points. Stop order is optimized for shortest
            driving time (Google Directions), and the route follows actual roads.
          </Typography>

          {simError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {simError}
            </Alert>
          )}

          {endpointOptions.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No start/end points available. Add a fuel depot on the map or enable driver location.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <FormControl fullWidth size="small">
                <InputLabel id="sim-start-label">Start</InputLabel>
                <Select
                  labelId="sim-start-label"
                  label="Start"
                  value={startId}
                  onChange={(e) => setStartId(e.target.value)}
                >
                  {endpointOptions.map((opt) => (
                    <MenuItem key={`start-${opt.id}`} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="sim-end-label">End</InputLabel>
                <Select
                  labelId="sim-end-label"
                  label="End"
                  value={endId}
                  onChange={(e) => setEndId(e.target.value)}
                >
                  {endpointOptions.map((opt) => (
                    <MenuItem key={`end-${opt.id}`} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Active jobs ({simulatableJobs.length} on map)
          </Typography>
          {simulatableJobs.length === 0 ? (
            <Alert severity="info">No geocoded active jobs to include in the simulation.</Alert>
          ) : (
            <FormGroup sx={{ maxHeight: 280, overflow: 'auto' }}>
              {simulatableJobs.map((job) => (
                <FormControlLabel
                  key={job.id}
                  control={
                    <Checkbox
                      checked={selectedJobIds.includes(job.id)}
                      onChange={() => toggleJob(job.id)}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {job.label}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRunSimulation}
            disabled={simulating || !endpointOptions.length || !simulatableJobs.length}
          >
            {simulating ? 'Computing route…' : 'Run simulation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Render inside GoogleMap when simulation is active. */
export function MapRouteSimulationLayer({ simulation }) {
  if (!simulation?.path?.length) return null;

  const routeMarkerIcon = (text, fillColor) => {
    if (!window.google?.maps) return undefined;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 11,
      fillColor,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      labelOrigin: new window.google.maps.Point(0, 0),
    };
  };

  return (
    <>
      <Polyline
        path={simulation.path}
        options={{
          strokeColor: '#7B1FA2',
          strokeOpacity: 0.92,
          strokeWeight: 5,
          geodesic: false,
          zIndex: 50,
        }}
      />
      <Marker
        position={simulation.start}
        title={`Start: ${simulation.start.label}`}
        icon={routeMarkerIcon('S', '#2E7D32')}
        label={{ text: 'S', color: '#ffffff', fontWeight: 'bold', fontSize: '11px' }}
        zIndex={60}
      />
      {simulation.stops.map((stop) => (
        <Marker
          key={`sim-stop-${stop.id}`}
          position={stop.position}
          title={`Stop ${stop.order}: ${stop.label}`}
          icon={routeMarkerIcon(String(stop.order), '#7B1FA2')}
          label={{
            text: String(stop.order),
            color: '#ffffff',
            fontWeight: 'bold',
            fontSize: '10px',
          }}
          zIndex={55}
        />
      ))}
      <Marker
        position={simulation.end}
        title={`End: ${simulation.end.label}`}
        icon={routeMarkerIcon('E', '#C62828')}
        label={{ text: 'E', color: '#ffffff', fontWeight: 'bold', fontSize: '11px' }}
        zIndex={60}
      />
    </>
  );
}
